import React, { useMemo, useState } from 'react';
import { BsEyeFill, BsEyeSlash, BsPlus, BsX } from 'react-icons/bs';
import { Box, Button, Divider, HStack, Icon, IconButton, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { menuItemBaseStyle } from '../../styles/theme/components/menu';
import colors from '../../styles/theme/foundation/colors';
import { createMap } from '../../utils/arrayUtil';
import { getUserFullName } from '../../utils/userNameUtil';
import InfoIconTooltip from '../InfoIconTooltip';
import { UserIcon } from '../library';
import SearchInputGroup from '../SearchInputGroup';

const WatcherIconButton = ({
  watchers,
  currentUser,
  isWatcher,
  canManageWatchers,
  createWatcher,
  deleteWatcher,
  companyUsers,
  companyUsersLoading,
  buttonSize = 'sm',
  stopPropagation = false,
  placement = 'bottom',
  maxHeight = '200px',
  toolTip = null
}) => {
  const [showCreateWatcherSearch, setShowCreateWatcherSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const watcherMap = useMemo(() => createMap(watchers, 'userId'), [watchers]);

  const users = useMemo(() => {
    if (companyUsersLoading) {
      return [];
    }

    if (searchTerm === '' || searchTerm === undefined) {
      return [];
    }

    const searchValue = searchTerm.toLowerCase();

    if (Array.isArray(companyUsers)) {
      return companyUsers.filter(i => {
        if (watcherMap[i.userId]) {
          return false;
        }

        const userFullName = getUserFullName(i.userFirstName, i.userLastName);

        return userFullName.toLowerCase().includes(searchValue);
      });
    }

    return [];
  }, [companyUsers, companyUsersLoading, searchTerm, watcherMap]);

  const handleIconClick = e => {
    if (stopPropagation) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (isWatcher) {
      deleteWatcher(currentUser);
    } else {
      createWatcher(currentUser);
    }
  };

  return (
    <HStack>
      <Popover
        placement={placement}
        trigger="hover"
        onClose={() => {
          setSearchTerm('');
          setShowCreateWatcherSearch(false);
        }}
      >
        <PopoverTrigger>
          <IconButton
            aria-label="watcher-eye-icon-button"
            icon={<Icon as={isWatcher ? BsEyeFill : BsEyeSlash} />}
            size={buttonSize}
            variant={isWatcher ? 'textPrimary' : 'textGrey'}
            onClick={handleIconClick}
          />
        </PopoverTrigger>
        <PopoverContent
          zIndex="2000"
          onClick={e => {
            if (stopPropagation) {
              e.stopPropagation();
              e.preventDefault();
            }
          }}
        >
          <PopoverArrow />
          <PopoverBody maxW="300px" p={3}>
            {watchers.length > 0
              ? (
                <Box>
                  <HStack justifyContent="space-between">
                    <Text textStyle="bodySm500">{`Current Watchers (${watchers.length})`}</Text>
                    {toolTip && <InfoIconTooltip toolTip={toolTip} />}
                  </HStack>
                  <Divider my={2} />
                  <Box maxHeight={maxHeight} maxWidth="250px" overflowY="auto">
                    {watchers.map(watcher => (
                      <HStack justifyContent="space-between" key={watcher.id} mb={2}>
                        <HStack maxW="85%">
                          <UserIcon
                            firstName={watcher.user.userFirstName}
                            lastName={watcher.user.userLastName}
                          />
                          <Text noOfLines={1} textStyle="bodySm">{`${watcher.user.userFirstName} ${watcher.user.userLastName}`}</Text>
                        </HStack>
                        {canManageWatchers && (
                          <IconButton
                            aria-label={`delete-user-${watcher.userId}`}
                            icon={<Icon as={BsX} />}
                            size="sm"
                            variant="textGrey"
                            onClick={() => deleteWatcher(watcher.user)}
                          />
                        )}
                      </HStack>
                    ))}
                  </Box>
                </Box>
              ) : (
                <HStack justifyContent="space-between">
                  <Text textStyle="bodySm">No watchers</Text>
                  {toolTip && <InfoIconTooltip toolTip={toolTip} />}
                </HStack>
              )}

            {canManageWatchers && (
              <Box>
                <Divider my={2} />
                {!showCreateWatcherSearch
                  ? (
                    <Button
                      leftIcon={<Icon as={BsPlus} />}
                      variant="secondary"
                      w="100%"
                      onClick={() => setShowCreateWatcherSearch(true)}
                    >
                      Add Watcher
                    </Button>
                  ) : (
                    <HStack mb={2}>
                      <SearchInputGroup
                        dataAnalyticsId="create-watcher-search-input"
                        isDisabled={false}
                        searchTerm={searchTerm}
                        variant="primary"
                        width="100%"
                        onChange={e => {
                          setSearchTerm(e.target.value);
                        }}
                      />
                      <IconButton
                        aria-label="cancel-create-watcher-icon-button"
                        icon={<Icon as={BsX} />}
                        size="sm"
                        variant="textGrey"
                        onClick={() => setShowCreateWatcherSearch(false)}
                      />
                    </HStack>
                  )}
                {!!users.length && (
                  <Box maxH="160px" overflowY="auto">
                    {users.map(u => (
                      <HStack
                        _hover={{
                          cursor: 'pointer',
                          bgColor: colors.primaryScale['50'],
                          color: colors.primaryScale['600']
                        }}
                        _selected={menuItemBaseStyle._focus} //eslint-disable-line
                        height="40px"
                        key={u.userId}
                        py="10px"
                        onClick={() => {
                          createWatcher(u);
                          setSearchTerm('');
                          setShowCreateWatcherSearch(false);
                        }}
                      >
                        <UserIcon
                          firstName={u.userFirstName}
                          lastName={u.userLastName}
                        />
                        <Text noOfLines={1} textStyle="bodySm">{`${u.userFirstName} ${u.userLastName}`}</Text>
                      </HStack>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </HStack>
  );
};

WatcherIconButton.propTypes = {
  watchers: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentUser: PropTypes.shape({ userId: PropTypes.number }),
  isWatcher: PropTypes.bool.isRequired,
  canManageWatchers: PropTypes.bool,
  createWatcher: PropTypes.func.isRequired,
  deleteWatcher: PropTypes.func.isRequired,
  companyUsers: PropTypes.arrayOf(PropTypes.shape({ userId: PropTypes.number, userFirstName: PropTypes.string, userLastName: PropTypes.string })),
  companyUsersLoading: PropTypes.bool,
  buttonSize: PropTypes.string,
  stopPropagation: PropTypes.bool,
  placement: PropTypes.string,
  maxHeight: PropTypes.string,
  toolTip: PropTypes.string
};

export default WatcherIconButton;
